/**
 * Feature Flags Context
 * Provides feature flag state and access checking across the app
 */

import React, { createContext, useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { authFetch } from '../utils/api';
import { isAdminRole } from '../utils/planAccess';
import { toUserFacingError, ERROR_FALLBACKS } from '../utils/apiErrorHandler';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const FeatureFlagsContext = createContext();

export const FeatureFlagsProvider = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth();
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch feature flags from API
  const fetchFeatures = useCallback(async () => {
    if (!isAuthenticated) {
      setFeatures({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await authFetch(`${API_BASE}/feature-flags`, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch features');
      }

      const data = await response.json();

      // Convert array to object keyed by feature key
      const featuresMap = {};
      data.features.forEach(feature => {
        featuresMap[feature.key] = feature;
      });

      setFeatures(featuresMap);
      setError(null);
    } catch (err) {
      console.error('Error fetching feature flags:', err);
      // Nothing consumes this today, but it is exposed on the context value,
      // so gate it rather than leave a raw message waiting to be rendered.
      setError(toUserFacingError(err, ERROR_FALLBACKS.load));
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Key on WHO, never on the user OBJECT. `AuthContext.storeUser` replaces
  // `currentUser` with a brand-new object on every profile load — initAuth,
  // refreshUser, verifySession, the OAuth callback — so an effect keyed on the
  // object refetched the flags on each of those. Combined with anything that
  // touched auth repeatedly, that turned into a flood of /api/feature-flags
  // large enough to exhaust the general per-IP rate-limit budget, after which
  // every request from that browser 429'd, the session refresh failed, and the
  // host was signed out and could not sign back in.
  // Same mistake, same fix as SocketContext — see its comment.
  const userId = currentUser?.id;
  // `hasFeature` reads nothing from the user but the role, so key it on that
  // too: on the object, every profile load gave it a new identity and
  // re-rendered every consumer (BroadcastStudio reads it three times).
  const role = currentUser?.role;
  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures, userId]);

  /**
   * Check if user has access to a feature
   * @param {string} featureKey - The feature key to check
   * @returns {boolean} - Whether user has access
   */
  const hasFeature = useCallback((featureKey) => {
    // Admins always have access
    if (isAdminRole(role)) {
      return true;
    }

    const feature = features[featureKey];
    if (!feature) {
      // If feature doesn't exist, assume no access
      return false;
    }

    return feature.hasAccess === true;
  }, [features, role]);

  /**
   * Get feature details including access reason
   * @param {string} featureKey - The feature key
   * @returns {Object} - Feature details with access info
   */
  const getFeature = useCallback((featureKey) => {
    return features[featureKey] || null;
  }, [features]);

  /**
   * Get the reason why a feature is inaccessible
   * @param {string} featureKey - The feature key
   * @returns {string|null} - Reason or null if accessible
   */
  const getAccessReason = useCallback((featureKey) => {
    const feature = features[featureKey];
    if (!feature || feature.hasAccess) {
      return null;
    }
    return feature.reason || 'Feature not available';
  }, [features]);

  /**
   * Track feature usage
   * @param {string} featureKey - The feature key to track
   */
  const trackUsage = useCallback(async (featureKey) => {
    if (!isAuthenticated) return;

    try {
      await authFetch(`${API_BASE}/feature-flags/${featureKey}/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (err) {
      console.error('Error tracking feature usage:', err);
    }
  }, [isAuthenticated]);

  /**
   * Get all features grouped by category
   * @returns {Object} - Features grouped by category
   */
  const getFeaturesByCategory = useCallback(() => {
    const categories = {};
    Object.values(features).forEach(feature => {
      const category = feature.category || 'other';
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(feature);
    });
    return categories;
  }, [features]);

  /**
   * Refresh features from server
   */
  const refresh = useCallback(() => {
    return fetchFeatures();
  }, [fetchFeatures]);

  // Memoized so a provider render can't re-render every consumer on its own.
  const value = useMemo(() => ({
    features,
    loading,
    error,
    hasFeature,
    getFeature,
    getAccessReason,
    trackUsage,
    getFeaturesByCategory,
    refresh
  }), [features, loading, error, hasFeature, getFeature, getAccessReason, trackUsage, getFeaturesByCategory, refresh]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/**
 * Hook to access feature flags
 */
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

/**
 * Hook to check a single feature
 * @param {string} featureKey - The feature key to check
 * @returns {Object} - { hasAccess, reason, loading, feature }
 */
export const useFeature = (featureKey) => {
  const { hasFeature, getFeature, getAccessReason, loading } = useFeatureFlags();

  return {
    hasAccess: hasFeature(featureKey),
    reason: getAccessReason(featureKey),
    loading,
    feature: getFeature(featureKey)
  };
};

export default FeatureFlagsContext;
